import { useCallback } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  AccountAutoComplete,
  SitesDropdown,
  StringMapDropdown,
} from '@/components';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { RecipientsAutoComplete } from '@/components/common/recipients-auto-complete';
import {
  StringMapAttributeName,
  StringMapObjectTypeCode,
} from '@/components/common/string-map-dropdown/constants';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SEX_EMBRYO } from '@/constants';
import { FormField } from '@/pages/embryo-details/components/FormField';
import {
  ImplantedEmbryoDto,
  ImplantedEmbryoInput,
  useEditImplantedEmbryosMutation,
} from '@/services/embryo-service';
import { useToast } from '@/services/toast';

const sexOptions = Object.entries(SEX_EMBRYO).map(([label, value]) => ({
  label,
  value,
}));
const statusOptions = [
  { label: 'Active', value: 0 },
  { label: 'Inactive', value: 1 },
];
const CLINIC_RANCH_HAUL_IN_OPTIONS = [
  { label: 'Clinic', value: 1 },
  { label: 'Ranch', value: 2 },
  { label: 'Haul-In', value: 3 },
];

const formFields = [
  {
    label: 'Status',
    name: 'stateCode',
    component: Dropdown,
    componentProps: { options: statusOptions },
  },
  { label: 'Recip Tag', name: 'recipient', component: InputText },
  { label: 'Color', name: 'color', component: InputText },
  { label: 'Parity', name: 'parity', component: InputText },
  { label: 'Straw', name: 'straw', component: InputNumber },
  { label: 'Embryo Tag', name: 'embryoTag', component: InputNumber },
  { label: 'Stage', name: 'stage', component: InputNumber },
  { label: 'Grade', name: 'grade', component: InputNumber },
  {
    label: 'Sex',
    name: 'sex',
    component: Dropdown,
    componentProps: { options: sexOptions },
  },
  { label: 'Estrus Sync', name: 'estrusSync', component: InputNumber },
  {
    label: 'Transfer Quality',
    name: 'transferQuality',
    component: InputNumber,
  },
  { label: 'Trans Dist', name: 'transDist', component: InputNumber },
  {
    label: 'CL',
    name: 'clValue',
    component: StringMapDropdown,
    componentProps: {
      attributeName: StringMapAttributeName.RIGHT_LEFT,
      objectTypeCode: StringMapObjectTypeCode.RIGHT_LEFT,
      appendTo: undefined,
    },
  },
  {
    label: 'Clinic Ranch Haulin',
    name: 'clinicRanchHaulin',
    component: Dropdown,
    componentProps: { options: CLINIC_RANCH_HAUL_IN_OPTIONS },
  },
  {
    label: 'Recip',
    name: 'recipientId',
    component: RecipientsAutoComplete,
    componentProps: { appendTo: undefined },
  },
  { label: 'Comment', name: 'comment', component: InputText },
  { label: 'Frozen Grade', name: 'frozenGrade', component: InputNumber },
  { label: 'Frozen Stage', name: 'frozenStage', component: InputNumber },
  { label: 'Transfer Tech', name: 'transferTech', component: InputText },
  {
    label: 'Implant Type',
    name: 'calfType',
    component: StringMapDropdown,
    componentProps: {
      attributeName: StringMapAttributeName.CALF_TYPE,
      objectTypeCode: StringMapObjectTypeCode.CALF_TYPE,
      appendTo: undefined,
    },
  },
  { label: 'Created', name: 'created', component: Calendar },
  {
    label: 'Owner',
    name: 'ownerId',
    component: AccountAutoComplete,
    componentProps: { appendTo: null },
  },
  {
    label: 'Site',
    name: 'siteId',
    component: SitesDropdown,
    componentProps: { appendTo: undefined },
  },
];

const implantedEmbryoEditSchema = z.object({
  grade: z.number().nullable().optional(),
  frozenGrade: z.number().nullable().optional(),
  sex: z.number().nullable().optional(),
  stage: z.number().nullable().optional(),
  frozenStage: z.number().nullable().optional(),
  straw: z.number().nullable().optional(),
  embryoTag: z.number().nullable().optional(),
  transferQuality: z.number().nullable().optional(),
  calfType: z.number().nullable().optional(),
  clinicRanchHaulin: z.number().nullable().optional(),
  estrusSync: z.number().nullable().optional(),
  transDist: z.number().nullable().optional(),
  clValue: z.number().nullable().optional(),
  parity: z.string().nullable().optional(),
  transferTech: z.string().nullable().optional(),
  recipient: z.string().nullable().optional(),
  recipientId: z.string().nullable().optional(),
  ownerId: z.string().nullable().optional(),
  siteId: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
  comment: z.string().nullable().optional(),
  stateCode: z.number().nullable().optional(),
  created: z.any().optional(),
});

type ImplantedEmbryoEditFormValues = z.infer<typeof implantedEmbryoEditSchema>;

type EditImplantedEmbryosDialogProps = {
  visible: boolean;
  selectedRows: ImplantedEmbryoDto[];
  onHide: () => void;
  onSuccess?: () => void;
  showEmbryoUpdateNote?: boolean;
};

export const EditImplantedEmbryosDialog = ({
  visible,
  selectedRows,
  onHide,
  onSuccess,
  showEmbryoUpdateNote = false,
}: EditImplantedEmbryosDialogProps) => {
  const toast = useToast();
  const { mutate: editImplantedEmbryos, isPending } =
    useEditImplantedEmbryosMutation();

  const { control, handleSubmit, reset } =
    useForm<ImplantedEmbryoEditFormValues>({
      resolver: zodResolver(implantedEmbryoEditSchema),
      defaultValues: {},
    });

  const onSubmit = useCallback(
    (formValues: ImplantedEmbryoEditFormValues) => {
      const changedFields = Object.fromEntries(
        Object.entries(formValues).filter(
          ([, value]) => value !== null && value !== undefined && value !== '',
        ),
      );

      if (Object.keys(changedFields).length === 0) {
        toast.show({
          severity: 'warn',
          summary: 'Warning',
          detail: 'No changes to apply',
          life: 3000,
        });
        return;
      }

      const payload: ImplantedEmbryoInput[] = selectedRows.map((row) => ({
        ...row,
        ...changedFields,
        id: row.id,
      }));

      const hasAssociatedEmbryo = selectedRows.some((row) => row.embryoId);

      editImplantedEmbryos(payload, {
        onSuccess: () => {
          const detail = hasAssociatedEmbryo
            ? `${selectedRows.length} implanted embryo(s) and associated embryo(s) updated successfully`
            : `${selectedRows.length} implanted embryo(s) updated successfully`;
          toast.show({
            severity: 'success',
            summary: 'Success',
            detail,
            life: 3000,
          });
          reset({});
          onSuccess?.();
          onHide();
        },
        onError: (error: Error) => {
          toast.show({
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Failed to update implanted embryos',
            life: 5000,
          });
        },
      });
    },
    [selectedRows, editImplantedEmbryos, toast, reset, onSuccess, onHide],
  );

  const handleCancel = useCallback(() => {
    reset({});
    onHide();
  }, [reset, onHide]);

  return (
    <Dialog
      open={visible}
      onOpenChange={(o) => {
        if (!o) handleCancel();
      }}
    >
      <DialogContent className="md:max-w-[50vw] 2xl:max-w-[70vw]">
        <DialogHeader>
          <DialogTitle>
            {`Edit ${selectedRows.length} Implanted Embryo(s)`}
          </DialogTitle>
        </DialogHeader>
        <p className="mb-4 text-sm text-gray-500">
          Only filled fields will be applied to all selected rows.
          {showEmbryoUpdateNote &&
            ' Changes will also update the associated live embryo record if one exists.'}
        </p>
        <div className="grid grid-cols-3 gap-x-6 gap-y-1">
          {formFields.map((field) => (
            <FormField
              key={field.name}
              label={field.label}
              name={field.name}
              control={control}
              component={field.component}
              componentProps={field.componentProps}
            />
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit(onSubmit)}
            disabled={isPending}
          >
            {isPending && (
              <i className="pi pi-spin pi-spinner mr-1.5 text-xs" />
            )}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
